import React, { useState, useEffect, useRef } from 'react';
import * as api from '../utils/api';
import { curMonth, addMonth, monthLabel, monthFull, fmt, pct, lazySyncMonth } from './dashboard/helpers';
import OtaAnalytics from './dashboard/OtaAnalytics';
import BookingPace from './dashboard/BookingPace';
import BookingActivity, { PeriodNav } from './dashboard/BookingActivity';
import { useAuth } from '../context/AuthContext';
import { DESK_ROLES, isInvestor } from '../config/constants';
import './Dashboard.css';

// Роли, которым разрешён запуск синка TravelLine (POST /travelline/sync на
// бэке защищён тем же набором — adminOrManager).
const ADMIN_ROLES = new Set(['owner', 'director']);

const TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'ota',      label: 'OTA-аналитика' },
  { key: 'pace',     label: 'Темп бронирования' },
];

export default function Dashboard() {
  const { user } = useAuth();
  // Роли «стойки»: только «Обзор», зафиксированный на текущем месяце (§3.7).
  const lockMonth = DESK_ROLES.includes(user?.role);
  // Инвестор: только «Обзор» и только карточки отелей своего scope (сервер
  // отдаёт уже отфильтрованный список), без сводной панели «Все отели» и без
  // активности бронирования. OTA-аналитика и темп закрыты и на сервере (denyInvestor).
  const investor = isInvestor(user);
  const tabs = (lockMonth || investor) ? TABS.filter(t => t.key === 'overview') : TABS;
  const [tab, setTab] = useState('overview');
  return (
    <div className="dbx-page">
      <div className="dbx-head">
        <div>
          <span className="dbx-kicker">Сеть отелей Грандора</span>
          <h1 className="dbx-title">Дашборд</h1>
        </div>
      </div>
      {tabs.length > 1 && (
        <div className="dbx-tabs">
          {tabs.map(t => (
            <button key={t.key} type="button" className={tab === t.key ? 'is-active' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>
      )}
      {tab === 'overview'            && <Overview lockMonth={lockMonth} investor={investor} />}
      {!lockMonth && !investor && tab === 'ota'   && <OtaAnalytics />}
      {!lockMonth && !investor && tab === 'pace'  && <BookingPace />}
    </div>
  );
}

// Цвет выполнения плана: ≥100% зелёный, ≥70% жёлтый, иначе jasper. План=0 — нейтрально.
const doneColor = (val, plan) => {
  if (!plan) return 'var(--choco-300)';
  const r = val / plan;
  return r >= 1 ? 'var(--status-success)' : r >= 0.7 ? 'var(--status-warning)' : 'var(--jasper-600)';
};

// Цвет доли отмен: ≥25% jasper, ≥15% жёлтый, иначе нейтрально.
const cancColor = (rate) =>
  rate >= 0.25 ? 'var(--jasper-600)' : rate >= 0.15 ? 'var(--status-warning)' : 'var(--text-strong)';

// Сводные показатели по всем отелям (простые суммы + производные коэффициенты).
function aggregateCards(cards) {
  const r2 = x => Math.round((x || 0) * 100) / 100;
  const sum = f => cards.reduce((s, c) => s + (f(c) || 0), 0);
  const availRn = sum(c => c.available_rn), occRn = sum(c => c.occupied_rn);
  const booked = sum(c => c.booked_revenue), plan = sum(c => c.plan);
  return {
    rooms: sum(c => c.rooms),
    occupancy: availRn > 0 ? occRn / availRn : 0,
    adr: occRn > 0 ? r2(booked / occRn) : 0,
    revpar: availRn > 0 ? r2(booked / availRn) : 0,
    plan: r2(plan), booked_revenue: r2(booked), fact: r2(sum(c => c.fact)),
    plan_pickup_pct: plan > 0 ? r2(booked / plan) : null,
    potential_revenue: r2(sum(c => (c.potential ? c.potential.revenue : 0))),
    potential_empty: Math.round(sum(c => (c.potential ? c.potential.empty_rn : 0))),
    ytd_plan: r2(sum(c => (c.ytd ? c.ytd.plan : 0))),
    ytd_revenue: r2(sum(c => (c.ytd ? c.ytd.revenue : 0))),
    canc_net: r2(sum(c => c.cancellations.resale.net_loss)),
    canc_cancelled: sum(c => c.cancellations.cancelled),
    canc_total: sum(c => c.cancellations.total),
  };
}

// ─── Обзор ───────────────────────────────────────────────────────────────────
// Один месяц на всю страницу: панель периода над сводкой управляет и сводкой
// «Все отели», и карточками отелей (раньше у каждой карточки был свой
// переключатель — месяц сводки было не видно и не перелистнуть).
// lockMonth (роли «стойки») — месяц зафиксирован на текущем, навигации нет.
function Overview({ lockMonth = false, investor = false }) {
  const { user } = useAuth();
  const canSync = user && ADMIN_ROLES.has(user.role);
  const [cards, setCards]       = useState(null);
  const [baseMonth, setBaseMonth] = useState(curMonth());
  const [loading, setLoading]   = useState(true);
  // При перевале месяца автоматически переходим на новый только если смотрели
  // текущий — не сбиваем просмотр прошлого месяца.
  const followCur = useRef(true);
  const pickMonth = m => { followCur.current = m === curMonth(); setBaseMonth(m); };

  // Все карточки одним запросом (меньше round-trip на высоколатентном канале).
  // Дашборд держат открытым сутками: day-зависимые цифры («Потенциал ещё»)
  // считаются на сервере от «сегодня» — рефетчим при возврате на вкладку.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = () => api.get(`/reports/dashboard/cards?month=${baseMonth}`)
      .then(d => { if (alive) { setCards(d.hotels || []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    load();
    // Месяц вне рабочего окна TravelLine-синка — фоновая подгрузка броней,
    // затем тихий рефетч (иначе «Брони на руках» из устаревших tl_bookings).
    lazySyncMonth(baseMonth, canSync, () => { if (alive) load(); });

    const refresh = () => {
      const cm = curMonth();
      if ((followCur.current || lockMonth) && cm !== baseMonth) { setBaseMonth(cm); return; }
      load();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [baseMonth, lockMonth, canSync]);

  // Средние оценки отзывов — отдельным запросом: внешний сервис отзывов не
  // должен задерживать карточки. Пока грузится — карточки без блока оценок.
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    let alive = true;
    setRatings({});
    api.get(`/reports/dashboard/ratings?month=${baseMonth}`)
      .then(d => { if (alive) setRatings(Object.fromEntries((d.hotels || []).map(h => [h.property_id, h]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [baseMonth]);

  // Максимум строк «год к году» среди карточек — одинаковая высота секции.
  const maxYoy = cards ? Math.max(0, ...cards.map(c => (c.yoy || []).length)) : 0;
  const total = (cards && cards.length) ? aggregateCards(cards) : null;
  const isCur = baseMonth === curMonth();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {!investor && <BookingActivity />}

      <section className="dbx-section">
        <div className="dbx-section__head">
          <div>
            <span className="dbx-kicker">Отели · месяц</span>
            <h2 className="dbx-section__title">{monthFull(baseMonth)}</h2>
            <div className="dbx-section__sub">{isCur ? 'Текущий месяц — данные на сегодня' : 'Прошедший или будущий месяц'}</div>
          </div>
          {!lockMonth && (
            <PeriodNav
              label={monthFull(baseMonth)}
              onPrev={() => pickMonth(addMonth(baseMonth, -1))}
              onNext={() => pickMonth(addMonth(baseMonth, 1))}
              onReset={isCur ? null : () => pickMonth(curMonth())}
              resetLabel="Текущий месяц"
            />
          )}
        </div>

        {loading && !cards && <p className="dbx-note">Загрузка…</p>}
        {cards && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, opacity: loading ? 0.5 : 1, transition: 'opacity var(--transition-fast)' }}>
            {!investor && <SummaryPanel total={total} month={baseMonth} lockMonth={lockMonth} />}
            <div className="dbx-hotels">
              {cards.map(card => <HotelCard key={card.property_id} initial={card} month={baseMonth} maxYoy={maxYoy} lockMonth={lockMonth} rating={ratings[card.property_id]} />)}
            </div>
          </div>
        )}
        <p className="dbx-note">
          Загрузка, ADR, RevPAR и «брони на руках» — по активным броням TravelLine, задевающим месяц (методология TravelLine).
          Факт — признанная выручка проживания (P&L). Выкуп плана — брони на руках / план. Отмены — брони с заездом в выбранном месяце.
          План задаётся в Справочниках → «Планы выручки».
        </p>
      </section>
    </div>
  );
}

// Метрика карточки отеля.
function Metric({ label, value, hint, color }) {
  return (
    <div className="dbx-metric">
      <span className="dbx-lbl">{label}</span>
      <span className="dbx-metric__value" style={color ? { color } : undefined}>{value}</span>
      <span className="dbx-metric__hint">{hint || ' '}</span>
    </div>
  );
}

// Строка денежного блока: подпись (+ подсказка) слева, сумма справа.
function Kv({ label, value, hint, color }) {
  return (
    <div className="dbx-kv__row">
      <div className="dbx-kv__label">
        <span className="dbx-lbl">{label}</span>
        {hint && <span className="dbx-kv__hint">{hint}</span>}
      </div>
      <span className="dbx-kv__value" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="dbx-block">
      <div className="dbx-block__title">{title}</div>
      {children}
    </div>
  );
}

function DkKpi({ label, value, sub, accent }) {
  return (
    <div className="dbx-kpi">
      <span className="dbx-lbl">{label}</span>
      <span className={'dbx-kpi__value' + (accent ? ' is-accent' : '')}>{value}</span>
      <span className="dbx-kpi__sub">{sub || ' '}</span>
    </div>
  );
}

// Сводка по всем отелям — тёмная панель над карточками.
// lockMonth (роли «стойки») — без годового нарастающего итога («План YTD»).
function SummaryPanel({ total, month, lockMonth = false }) {
  if (!total) return null;
  const r = total;
  return (
    <div className="dbx-card dbx-card--dark dbx-summary">
      <div className="dbx-summary__head">
        <div className="dbx-summary__title">Все отели · <em>{monthFull(month)}</em></div>
        <div className="dbx-summary__meta">{r.rooms} номеров</div>
      </div>
      <div className="dbx-kpis">
        <DkKpi label="Загрузка" value={pct(r.occupancy)} />
        <DkKpi label="ADR" value={fmt(r.adr)} />
        <DkKpi label="RevPAR" value={fmt(r.revpar)} />
        <DkKpi label="План" value={fmt(r.plan)} />
        <DkKpi label="Брони на руках" value={fmt(r.booked_revenue)} />
        <DkKpi label="Выкуп плана" value={r.plan_pickup_pct != null ? pct(r.plan_pickup_pct) : '—'} accent />
        <DkKpi label="Потенциал ещё" value={fmt(r.potential_revenue)} sub={`${r.potential_empty} св. ночей`} />
        {!lockMonth && <DkKpi label="План с начала года" value={r.ytd_plan > 0 ? pct(r.ytd_revenue / r.ytd_plan) : '—'} sub={`${fmt(r.ytd_revenue)} из ${fmt(r.ytd_plan)}`} />}
        <DkKpi label="Потери от отмен" value={fmt(r.canc_net)} sub={`${r.canc_cancelled} из ${r.canc_total} броней`} />
      </div>
    </div>
  );
}

// ─── Карточка отеля ──────────────────────────────────────────────────────────
// Месяц — общий для страницы (month). Отдельный запрос — только при выборе
// категории номеров (напр. Апартаменты Н44); весь объект приходит в initial.
function HotelCard({ initial, month, maxYoy = 0, lockMonth = false, rating }) {
  const [d, setD]             = useState(initial);
  const [loading, setLoading] = useState(false);
  // '' — весь объект; иначе key категории (из initial.room_categories).
  const [scope, setScope]     = useState('');
  const categories = initial.room_categories || [];

  useEffect(() => {
    if (!scope) { setD(initial); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get(`/reports/dashboard/hotel?property_id=${initial.property_id}&month=${month}&room_category=${encodeURIComponent(scope)}`)
      .then(r => { if (alive) { setD(r); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, initial, scope]);

  const c = d?.cancellations;
  const name = initial.short_name || initial.name;

  return (
    <div className="dbx-card dbx-hcard">
      <div className="dbx-hcard__head">
        <div className="dbx-hcard__name">
          <span className="dbx-hotel">{name}</span>
          {categories.length > 0 && [{ key: '', label: name, title: 'Весь объект' }, ...categories].map(cat => (
            <button key={cat.key || 'all'} type="button" title={cat.title} onClick={() => setScope(cat.key)}
              className={'dbx-chip' + (scope === cat.key ? ' is-active' : '')}>{cat.label}</button>
          ))}
        </div>
        <span className="dbx-hcard__rooms">{d ? (d.room_category && d.rooms === 1 ? '1 номер' : `${d.rooms} номеров`) : ' '}</span>
      </div>

      <RatingBlock rating={rating} month={month} />

      {loading && <p className="dbx-note" style={{ margin: 0 }}>Загрузка…</p>}
      {!loading && d && (
        <>
          <div className="dbx-metrics">
            <Metric label="Загрузка" value={pct(d.occupancy)} hint={`${Math.round(d.occupied_rn)} из ${d.available_rn} ночей`} />
            <Metric label="ADR" value={fmt(d.adr)} />
            <Metric label="RevPAR" value={fmt(d.revpar)} />
          </div>

          <Block title="Выручка">
            <div className="dbx-kv">
              <Kv label="План" value={d.plan ? fmt(d.plan) : '—'} />
              <Kv label="Брони на руках" value={fmt(d.booked_revenue)} />
              <Kv label="Факт" value={fmt(d.fact)} hint={d.plan ? `${pct(d.fact / d.plan)} плана` : ''} color={doneColor(d.fact, d.plan)} />
            </div>
            {!d.room_category && (
              <div className="dbx-strip">
                <span className="dbx-strip__label">Выкуп плана <span>— брони / план</span></span>
                <span className="dbx-strip__value" style={{ color: doneColor(d.booked_revenue, d.plan) }}>
                  {d.plan_pickup_pct != null ? pct(d.plan_pickup_pct) : '—'}
                </span>
              </div>
            )}
            {d.potential && d.potential.remaining_days > 0 && (
              <div className="dbx-row">
                <span>Потенциал ещё <span>{d.potential.empty_rn} св. ночей{d.potential.adr ? ' × ' + fmt(d.potential.adr) : ''}</span></span>
                <b style={{ color: 'var(--status-success)' }}>{d.potential.revenue != null ? fmt(d.potential.revenue) : '—'}</b>
              </div>
            )}
          </Block>

          {/* Нарастающим итогом с начала года. Скрыто для ролей «стойки». */}
          {!lockMonth && d.ytd && !d.room_category && (
            <Block title="С начала года">
              <div className="dbx-kv">
                <Kv label="План" value={fmt(d.ytd.plan)} />
                <Kv label="Брони на руках" value={fmt(d.ytd.revenue)} />
                <Kv label="Выполнение" hint={`${monthLabel(d.ytd.from)} – ${monthLabel(d.ytd.to)}`} value={d.ytd.pct != null ? pct(d.ytd.pct) : '—'} color={doneColor(d.ytd.revenue, d.ytd.plan)} />
              </div>
            </Block>
          )}

          {/* Год к году — выручка того же месяца в прошлые годы. Скрыто для «стойки». */}
          {!lockMonth && maxYoy > 0 && (
            <Block title="Год к году · выручка">
              <table className="dbx-mini">
                <tbody>
                  <tr className="is-cur">
                    <td>{monthLabel(d.month)} <span style={{ color: 'var(--choco-300)', fontWeight: 400 }}>— сейчас</span></td>
                    <td>{fmt(d.booked_revenue)}</td>
                    <td style={{ width: 64 }}></td>
                  </tr>
                  {(d.yoy || []).map(y => (
                    <tr key={y.ym}>
                      <td>{monthLabel(y.ym)}</td>
                      <td style={{ color: 'var(--choco-700)' }}>{fmt(y.revenue)}</td>
                      <td><DeltaPct v={y.delta_pct} /></td>
                    </tr>
                  ))}
                  {Array.from({ length: Math.max(0, maxYoy - (d.yoy ? d.yoy.length : 0)) }).map((_, i) => (
                    <tr key={`yf${i}`} aria-hidden="true"><td style={{ color: 'transparent' }}>—</td><td /><td /></tr>
                  ))}
                </tbody>
              </table>
            </Block>
          )}

          <Block title="Отмены · заезд в этом месяце">
            <div className="dbx-row">
              <span>Отменено <b style={{ fontSize: 16, color: cancColor(c.rate) }}>{c.cancelled} из {c.total}</b> <span>({pct(c.rate)} броней) · штрафы собрано {fmt(c.penalty_captured)}</span></span>
            </div>
            <div className="dbx-kv">
              <Kv label="Сумма отмен" value={fmt((c.resale || {}).gross_lost)} />
              <Kv label="Продано повторно" value={fmt((c.resale || {}).recovered)}
                hint={(c.resale && c.resale.recovery_pct != null) ? `${pct(c.resale.recovery_pct)} от суммы` : ''} color="var(--status-success)" />
              <Kv label="Чистая потеря" value={fmt((c.resale || {}).net_loss)}
                color={(c.resale ? c.resale.net_loss : 0) > 0 ? 'var(--jasper-600)' : undefined} />
            </div>
            {c.by_channel.length > 0 && (
              <table className="dbx-mini">
                <thead><tr><th>Канал</th><th>Отмен</th><th>Потеряно</th><th>Доля</th></tr></thead>
                <tbody>
                  {c.by_channel.map(ch => (
                    <tr key={ch.kind}>
                      <td>{ch.label}</td>
                      <td>{ch.cnt}</td>
                      <td style={{ color: 'var(--jasper-600)' }}>{fmt(ch.lost_rev)}</td>
                      <td style={{ color: cancColor(ch.rate) }}>{pct(ch.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Block>
        </>
      )}
    </div>
  );
}

// ─── Оценки отзывов: крупно за месяц, в скобках — накопленная средняя на сейчас ─
// Шкалы раздельно, как в KPI сервиса отзывов: «из 5» — площадки с 5-балльной
// шкалой (Яндекс, 2ГИС…), «из 10» — с 10-балльной (Островок, Booking…).
const fmtR = v => (v == null ? '—' : v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Порог доли отзывов от выездов: ниже — красный, от него и выше — зелёный.
const REVIEW_SHARE_TARGET = 0.15;
const plural = (n, a, b, c) => { const m = n % 10, h = n % 100; return m === 1 && h !== 11 ? a : m >= 2 && m <= 4 && (h < 12 || h > 14) ? b : c; };

// Главное — оценки (крупно, по половинам плашки, выровнены влево, как блоки ниже); под ними одной фразой
// «27 выездов, 11,5% оставили 3 отзыва».
function RatingBlock({ rating, month }) {
  if (!rating) return <div className="dbx-rating is-empty" aria-hidden="true" />;
  if (rating.error) return <div className="dbx-rating"><span className="dbx-note">{rating.error}</span></div>;
  const m = rating.month || {}, a = rating.all || {};
  const n = m.n || 0;
  const shareCls = m.share == null ? '' : m.share < REVIEW_SHARE_TARGET ? ' is-down' : ' is-up';
  const reviews = <><b>{n}</b> {plural(n, 'отзыв', 'отзыва', 'отзывов')}</>;
  return (
    <div className="dbx-rating">
      <div className="dbx-block__title">Отзывы · {monthFull(month)}</div>
      <span className="dbx-lbl">Оценка (за всё время)</span>
      <div className="dbx-rating__scores">
        <Score value={m.r5} total={a.r5} />
        <Score value={m.r10} total={a.r10} />
      </div>
      <div className="dbx-rating__foot">
        {m.checkouts != null && m.share != null
          ? <><b>{m.checkouts}</b> {plural(m.checkouts, 'выезд', 'выезда', 'выездов')}, <b className={'dbx-rating__share' + shareCls}>{(m.share * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%</b> {n === 1 ? 'оставил' : 'оставили'} {reviews}</>
          : reviews}
      </div>
    </div>
  );
}

// Оценка за месяц и накопленная — одним размером. Месяц ниже накопленной —
// красный, выше — зелёный (сравнение с точностью до сотых).
function Score({ value, total }) {
  const cmp = value == null || total == null ? 0 : Math.sign(Math.round(value * 100) - Math.round(total * 100));
  const cls = cmp > 0 ? ' is-up' : cmp < 0 ? ' is-down' : '';
  return (
    <div className="dbx-score">
      <span className={'dbx-score__value' + cls}>{fmtR(value)}</span>
      <span className="dbx-score__total">({fmtR(total)})</span>
    </div>
  );
}

function DeltaPct({ v }) {
  if (v == null) return <span className="dbx-delta is-flat">—</span>;
  return <span className={'dbx-delta ' + (v >= 0 ? 'is-good' : 'is-bad')}>{v >= 0 ? '+' : '−'}{pct(Math.abs(v))}</span>;
}
